import React, { useEffect, useState } from 'react';
import parse from 'html-react-parser';
import { useThemeMode } from '../../../../context/contextTheme';
import hexToRGBA from "../../../../utils/hexToRGBA";
import { getFeed } from "../../../../api/get/get-feed";

const RSSFeed = () => {
  const { theme } = useThemeMode();
  const [feedData, setFeedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true; 

    const fetchNews = async () => {
      setLoading(true);
      try {
        const data = await getFeed();
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(data, "text/xml");
        const parseError = xmlDoc.querySelector("parsererror");
        if (parseError) {
          throw new Error("Invalid XML payload format received.");
        }

        const items = xmlDoc.querySelectorAll("item");
        const feedItems = Array.from(items).map((item) => ({
          title: item.querySelector("title")?.textContent || "Untitled Security Update",
          description: item.querySelector("description")?.textContent || "",
          link: item.querySelector("link")?.textContent || "#",
          pubDate: item.querySelector("pubDate")?.textContent || null,
        }));

        if (isMounted) {
          setFeedData(feedItems);
          setError(null);
        }
      } catch (err) {
        console.error("RSS Fetch Error:", err);
        if (isMounted) {
          setError("Failed to synchronize with security feed.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchNews();
    return () => {
      isMounted = false; 
    };
  }, []);

  return (
    <div className="modern-feed-container px-3" style={{ maxHeight: '85vh', overflowY: 'auto' }}>
      
      {/* Dynamic Header */}
      <div className="sticky-top py-3 d-flex align-items-center justify-content-between" 
           style={{ backgroundColor: theme.background, zIndex: 10 }}>
        <div className="d-flex align-items-center gap-2">
          <span className="live-dot pulse"></span>
          <h5 className="fw-bold m-0" style={{ color: theme.textColor }}>
            Cybersecurity Intel
          </h5>
        </div>
        <span className="badge rounded-pill fw-normal" 
              style={{ backgroundColor: hexToRGBA(theme.primary, 0.1), color: theme.primary, fontSize: '0.7rem' }}>
          SOURCE: NCSC
        </span>
      </div>

      {loading ? (
        <div className="text-center py-5 opacity-50">
          <div className="spinner-border spinner-border-sm mb-2" style={{ color: theme.primary }}></div>
          <p className="small">Updating Feed...</p>
        </div>
      ) : error ? (
        <div className="text-center py-5">
           <i className="bi bi-shield-slash fs-2 opacity-25" style={{ color: theme.textColor }}></i>
           <p className="small mt-2 text-danger">{error}</p>
        </div>
      ) : (
        feedData.map((item, index) => (
          <div key={index} className="news-item-card mb-3 p-3 rounded-4 shadow-sm"
            style={{ 
              backgroundColor: hexToRGBA(theme.background, 0.6), 
              border: `1px solid ${hexToRGBA(theme.border, 0.5)}`,
              backdropFilter: 'blur(10px)'
            }}>
            <div className="d-flex flex-column h-100">
              <h6 className="fw-bold mb-2 leading-snug" style={{ color: theme.textColor, fontSize: '1rem' }}>
                {item.title}
              </h6>
              
              {item.description && (
                <div className="small mb-3 opacity-75" style={{ lineHeight: 1.5, color: theme.textColor }}>
                  {parse(item.description)}
                </div>
              )}

              <div className="mt-auto d-flex align-items-center justify-content-between">
                <a
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fw-bold small text-decoration-none d-inline-flex align-items-center gap-1"
                  style={{ color: theme.primary }}
                >
                  View Report <i className="bi bi-box-arrow-up-right ms-1" style={{ fontSize: '0.7rem' }}></i>
                </a>
                {item.pubDate && (
                  <span className="text-muted" style={{ fontSize: '0.65rem' }}>
                    {new Date(item.pubDate).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))
      )}

      <style>{`
        .modern-feed-container::-webkit-scrollbar { width: 4px; }
        .modern-feed-container::-webkit-scrollbar-thumb { background: ${hexToRGBA(theme.textColor, 0.1)}; border-radius: 10px; }
        
        .news-item-card {
          transition: transform 0.2s ease, border-color 0.2s ease;
        }
        .news-item-card:hover {
          transform: translateY(-2px);
          border-color: ${theme.primary} !important;
        }

        .live-dot {
          width: 8px;
          height: 8px;
          background-color: #2ecc71;
          border-radius: 50%;
          display: inline-block;
        }
        .pulse { animation: pulse-green 2s infinite; }
        @keyframes pulse-green {
          0% { transform: scale(0.95); box-shadow: 0 0 0 0 ${hexToRGBA('#2ecc71', 0.7)}; }
          70% { transform: scale(1); box-shadow: 0 0 0 6px ${hexToRGBA('#2ecc71', 0)}; }
          100% { transform: scale(0.95); box-shadow: 0 0 0 0 ${hexToRGBA('#2ecc71', 0)}; }
        }
        .leading-snug { line-height: 1.3; }
      `}</style>
    </div>
  );
};

export default RSSFeed;